import { useRef } from 'react';
import type { TFunction } from 'i18next';
import type { ISiteMapArea } from '@interfaces/ISiteMap';
import { useComboDismiss } from '@hooks/useComboDismiss';
import { StockEntryCombo } from './StockEntryCombo';
import styles from './StockEntryDialog.module.css';

interface IStockEntryAreaFieldProps {
  /** The SLS site map's areas for the configured base plan. Empty means the
   *  map has none (or no plan is configured) — the field locks to N/A. */
  areas: ISiteMapArea[];
  /** The chosen area's id, or '' for "not stored in an area". */
  value: string;
  onChange: (areaId: string) => void;
  /** The locked line when there are no areas to choose from — "N/A (floor)"
   *  when a base plan names the floor that has none, bare "N/A" otherwise. */
  naLabel: string;
  t: TFunction;
}

/**
 * Where on the SLS site map this material is stored — the unit field's exact
 * combo (`StockEntryCombo`), pick-only: areas are records, not free text, so
 * the input is read-only and only the list sets the value. With no areas
 * drawn on the currently selected floor there is nothing to pick — the field
 * locks and says so. The save moves the material between the areas'
 * `materials` lists (one area per material, the AreaDrawer's rule).
 */
export function StockEntryAreaField({ areas, value, onChange, naLabel, t }: IStockEntryAreaFieldProps) {
  const comboRef = useRef<HTMLDivElement | null>(null);
  const combo = useComboDismiss(comboRef);
  const noneLabel = t('logistics.stockDialog.areaNone');
  const selectedName = areas.find((area) => area.areaId === value)?.name ?? noneLabel;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor="stock-area">
        {t('logistics.stockDialog.areaStored')}
      </label>
      {areas.length === 0 ? (
        <span className={`${styles.input} ${styles.lockedField}`}>{naLabel}</span>
      ) : (
        <StockEntryCombo
          id="stock-area"
          comboRef={comboRef}
          open={combo.open}
          onBlurAway={() => combo.setOpen(false)}
          value={selectedName}
          className={`${styles.input} ${styles.comboInput}`}
          onInputClick={combo.toggle}
          onCaretClick={combo.toggle}
          /* Options carry the areaId as the pick value — duplicate free-text
             names stay distinct records instead of collapsing to the first
             match. */
          options={[
            { value: '', label: noneLabel },
            ...areas.map((area) => ({ value: area.areaId, label: area.name })),
          ]}
          onPick={(areaId) => {
            onChange(areaId);
            combo.setOpen(false);
          }}
        />
      )}
    </div>
  );
}
